import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner, Stack, Table } from 'react-bootstrap';
import { useChargingStore } from '../../api/charging/store';
import { ChargingRange, TransactionTypeCharging, CHARGE_TYPE_FIXED, CHARGE_TYPE_PERCENTAGE } from '../../api/charging/data';
import CStack from '../../components/CStack';
import SwitchControl from '../../components/SwitchControl';
import BaseControl from '../../components/BaseControl';
import Loading from '../../components/Loading';
import useChargingRangeModal from '../../hooks/useChargingRangeModal';
import { ReactComponent as PlusIcon } from "../../svgs/plus.svg";
import { ReactComponent as EditIcon } from "../../svgs/edit.svg";
import { ReactComponent as TrashIcon } from "../../svgs/trash.svg";
import Label from '../../components/Label';
import InputItem from '../../components/InputItem';
import { on } from 'events';

const Charging: React.FC = () => {
    const {
        loading,
        configuration,
        //transactionTypes,
        error,
        retrieveConfiguration,
        retrieveTransactionTypes,
        updateConfiguration,
        clearError
    } = useChargingStore();

    const { showAddDialog, showEditDialog } = useChargingRangeModal();
    
    const [editMode, setEditMode] = useState(false);
    const [applying, setApplying] = useState(false);
    const [editedConfigs, setEditedConfigs] = useState<TransactionTypeCharging[]>([]);

    useEffect(() => {
        retrieveConfiguration();
        retrieveTransactionTypes();
    }, []);

    useEffect(() => {
        if (configuration) {
            setEditedConfigs(configuration.configurations || []);
        } else {
            setEditedConfigs([]);
        }
    }, [configuration]);

    const getOrCreateConfig = (type: string, subType?: string): TransactionTypeCharging => {
        //console.log(`Getting or creating config for ${type}${subType ? ` - ${subType}` : ''}`,editedConfigs);
        const existing = editedConfigs.find(c => 
            c.transactionType === type && c.subType === (subType || '' || undefined)
        );
        
        if (existing) return existing;
        
        return {
            transactionType: type,
            subType: subType || '',
            ranges: [],
            enabled: false
        };
    };

    const updateConfig = (config: TransactionTypeCharging) => {
        const index = editedConfigs.findIndex(c => 
            c.transactionType === config.transactionType && 
            c.subType === config.subType
        );
        
        if (index >= 0) {
            const newConfigs = [...editedConfigs];
            newConfigs[index] = config;
            setEditedConfigs(newConfigs);
        } else {
            setEditedConfigs([...editedConfigs, config]);
        }
    };

    const handleAddRange = (type: string, subType?: string) => {
        showAddDialog((newRange) => {
            const config = getOrCreateConfig(type, subType);
            const updatedConfig = {
                ...config,
                ranges: [...config.ranges, newRange]
            };
            updateConfig(updatedConfig);
        });
    };

    const handleEditRange = (type: string, subType: string | undefined, range: ChargingRange) => {
        showEditDialog(range, (updatedRange) => {
            const config = getOrCreateConfig(type, subType);
            const updatedConfig = {
                ...config,
                ranges: config.ranges.map(r => r.id === range.id ? updatedRange : r)
            };
            updateConfig(updatedConfig);
        });
    };


    const handleDeleteRange = (type: string, subType: string | undefined, rangeId: string) => {
        const config = getOrCreateConfig(type, subType);
        const updatedConfig = {
            ...config,
            ranges: config.ranges.filter(r => r.id !== rangeId)
        };
        updateConfig(updatedConfig);
    };

    const handleToggleEnabled = (type: string, subType: string | undefined, enabled: boolean) => {
        const config = getOrCreateConfig(type, subType);
        const updatedConfig = {
            ...config,
            enabled
        };
        updateConfig(updatedConfig);
    };

    const onToggleEdit = (checked: boolean) => {
        if (!checked) {
            if (configuration) {
                setEditedConfigs(configuration.configurations || []);
            } else {
                setEditedConfigs([]);
            }
        }
        setEditMode(checked);
    };

    const onApplyChanges = async () => {
        setApplying(true);
        try {
            for (const config of editedConfigs) {
                await updateConfiguration(config);
            }
            await retrieveConfiguration();
            setEditMode(false);
        } catch (error) {
            console.error('Failed to save configurations:', error);
        }
        setApplying(false);
    };

    const renderChargeDisplay = (range: ChargingRange) => {
        if (range.chargeType === CHARGE_TYPE_PERCENTAGE) {
            return `P{range.charge}%`;
        }
        return `${'P'+range.charge.toFixed(2)}`;
    };

    const renderOtherTransactionSection = (type: string, label: string, subType?: string) => {
        const config = getOrCreateConfig(type, subType);
        //console.log(`Rendering ${label} section`, config);
        
        return (
            <CStack 
                collapsible={true}
                defaultExpanded={false}
                header={label}
                actions={editMode ? [{
                    icon: <PlusIcon width={20} height={20} />,
                    onClick: () => handleAddRange(type, subType)
                }] : []}
            >
                <Card style={{maxWidth: '600px'}}>
                    <Card.Body>
                        <Row>
                            <Col md={8}>
                                
                            </Col>
                            <Col md={4} className="text-end">
                                <Form.Check
                                    type="switch"
                                    label="Enabled"
                                    checked={config.enabled}
                                    disabled={!editMode}
                                    onChange={(e) => handleToggleEnabled(type, subType, e.target.checked)}
                                />
                            </Col>
                        </Row>
                        
                        {config.ranges.length > 0 ? (
                            <Table size="sm" className="mt-2">
                                <thead>
                                    <tr>
                                        <th>Min Amount</th>
                                        <th>Max Amount</th>
                                        <th>Charge</th>
                                        {editMode && <th style={{ width: '50px' }}>Action</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {config.ranges.map((range) => (
                                        <tr key={range.id} style={{ cursor: editMode ? 'pointer' : 'default' }}>
                                            <td onClick={() => editMode && handleEditRange(type, subType, range)}>P{range.minAmount.toFixed(2)}</td>
                                            <td onClick={() => editMode && handleEditRange(type, subType, range)}>P{range.maxAmount.toFixed(2)}</td>
                                            <td onClick={() => editMode && handleEditRange(type, subType, range)}>{renderChargeDisplay(range)}</td>
                                            {editMode && (
                                                <td>
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="text-primary p-0 me-2"
                                                        onClick={() => handleEditRange(type, subType, range)}
                                                    >
                                                        <EditIcon width={16} height={16} />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="link"
                                                        className="text-danger p-0"
                                                        onClick={() => handleDeleteRange(type, subType, range.id)}
                                                    >
                                                        <TrashIcon width={16} height={16} />
                                                    </Button>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <Label color='gray' size='medium' fontWeight='200'>No charging ranges configured</Label>
                        )}
                    </Card.Body>
                </Card>
            </CStack>
        );
    };

    const renderStatementOfAccountSection = () => {
        const config = getOrCreateConfig('soa');
        const value = config.ranges.length > 0 ? config.ranges[0].charge : 0;
        const onChangedValue = (id: string, val: any) => {
            const updatedRange = {
                ...config.ranges[0],
                charge: parseFloat(val) || 0
            };
            const updatedConfig = {
                ...config,
                ranges: [updatedRange]
            };
            updateConfig(updatedConfig);
        };
        return (
            <InputItem edit={editMode} 
                text='Statement of Account Charge' 
                type='number' 
                value={value.toString()}
                onChanged={onChangedValue}
                />
        );
    };

    if (loading && !configuration) {
        return <Loading expandHeight />;
    }

    return (
        <>
            {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                    {error}
                </Alert>
            )}

            <CStack>
                <SwitchControl text="Edit Mode" value={editMode} onChanged={onToggleEdit} />
                {editMode && (
                    <BaseControl text="Apply changes?">
                        <Stack direction="horizontal" gap={2}>
                            <Button size="sm" onClick={() => onToggleEdit(false)} variant="outline-secondary">
                                Cancel
                            </Button>
                            <Button size="sm" disabled={applying} onClick={onApplyChanges} variant="primary">
                                {!applying ? "Apply" : (
                                    <Stack direction="horizontal" gap={1}>
                                        <Spinner size="sm" />
                                        Applying
                                    </Stack>
                                )}
                            </Button>
                        </Stack>
                    </BaseControl>
                )}
                {renderStatementOfAccountSection()}
            </CStack>

            {renderOtherTransactionSection('fund_transfer', 'Fund Transfer - Same Bank', 'same_bank')}
            {renderOtherTransactionSection('fund_transfer', 'Fund Transfer - Other Bank', 'other_bank')}
            {renderOtherTransactionSection('bill_payment', 'Bill Payment')}
            {/* {renderOtherTransactionSection('atm_withdrawal', 'ATM Withdrawal')} */}
            {/* {renderOtherTransactionSection('balance_inquiry', 'Balance Inquiry')} */}

            

            {/* Add Range Modal is now managed by useUXStore */}
            {/* Edit Range Modal is now managed by useUXStore */}

        </>
    );
};

export default Charging;